import { Fragment, useEffect, useState } from 'react';
import { Box, Button, Divider, FormControl, Tooltip, Typography } from '@mui/material';
import { HelpOutlineOutlined as HelpOutlineOutlinedIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';

import { SectionHeading } from '../../components/SectionHeading';
import AmcUploadDataSetField from '../../components/Form/Fields/AmcUploadDataSetField';
import Column from '../../components/Column';
import Printable from '../../components/Printable';
import Row from '../../components/Row';
import StatusDate from '../../components/StatusDate';
import Utils from '../../components/Utils';

import { useAttributionPageContext } from '../../hooks/useAttributionPage';
import AdvertiserMatchbackMetrics from './AdvertiserMatchbackMetrics';
import AdvertiserMatchbackMoreMenu from './partials/AdvertiserMatchbackMoreMenu';
import AttributionExportMenuButton from './AttributionExportMenuButton';
import PolkMatchbackDataTabs from './PolkMatchbackDataTabs';
import CampaignStrategyTypeSetField from '../../components/Form/Fields/CampaignStrategyTypeSetField';
import { Code } from '../../types/Code';
import { useAttributionAdvertiser } from '../../hooks/AttributionAdvertiser';

// type VIEW = 'chart' | 'table';

interface AdvertiserMatchbackTabProps {
    showExportButton?: boolean;
}

export default function AdvertiserMatchbackTab({ showExportButton = true }: AdvertiserMatchbackTabProps) {
    const {
        advertiser,
        hasOneAdvertiser,
        attributionDate,
        attributionStatus,
        fetchAttributionStatus,
        advertiserSalesUpdateDate,
        fetchPolkSalesWorkflowSets,
        fetchPreviousPolkSalesWorkflowSets,
        attributionCriteria,
        setAttributionCriteria,
    } = useAttributionPageContext();

    const [selectedCampaignTypes, setSelectedCampaignTypes] = useState<Code[]>([]);

    useAttributionAdvertiser();

    useEffect(() => {
        fetchAttributionStatus();
    }, [fetchAttributionStatus]);

    useEffect(() => {
        fetchPolkSalesWorkflowSets();
    }, [fetchPolkSalesWorkflowSets]);

    useEffect(() => {
        fetchPreviousPolkSalesWorkflowSets();
    }, [fetchPreviousPolkSalesWorkflowSets]);

    return (
        <Column gap={3} sx={{ mt: (theme) => theme.spacing(1) }}>
            <Row alignItems="flex-start" justifyContent="space-between">
                <Row gap={0}>
                    <Row gap={1} alignItems="center" justifyContent="flex-start">
                        {/* {advertiser?.id && ( */}
                        <Fragment>
                            <FormControl style={{ minWidth: '250px' }}>
                                <AmcUploadDataSetField
                                    displayEmpty={false}
                                    criteria={{
                                        // ...attributionCriteria,
                                        agencyIds: attributionCriteria.agencyIds,
                                        advertiserIds: attributionCriteria.advertiserIds,
                                        oemIds: attributionCriteria.oemIds,
                                        oemProgramIds: attributionCriteria.oemProgramIds,
                                        agencyMarketRegions: attributionCriteria.agencyMarketRegions,
                                    }}
                                    value={attributionCriteria?.dataSetId || 'test_v2dataset'}
                                    onChange={(dataSet) => {
                                        setAttributionCriteria({
                                            ...attributionCriteria,
                                            dataSet: dataSet ? dataSet.name : null,
                                            dataSetId: dataSet ? dataSet.dataSet : null,
                                        });
                                    }}
                                />
                            </FormControl>

                            <FormControl style={{ minWidth: '250px' }}>
                                <CampaignStrategyTypeSetField
                                    value={selectedCampaignTypes}
                                    onChange={(strategies) => {
                                        setSelectedCampaignTypes(strategies);
                                        setAttributionCriteria({
                                            ...attributionCriteria,
                                            campaignStrategies: strategies, // or types.map(t => t.code) if you want just codes
                                        });
                                    }}
                                />
                            </FormControl>

                            <Row gap={0.5} alignItems="center" sx={{ width: 'auto' }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Data Source
                                </Typography>

                                <Tooltip title="Sales data provided by Advertiser">
                                    <HelpOutlineOutlinedIcon color="action" sx={{ fontSize: 16 }} />
                                </Tooltip>
                            </Row>
                        </Fragment>
                        {/* )} */}

                        {hasOneAdvertiser && advertiserSalesUpdateDate && (
                            <Fragment>
                                <StatusDate title="Last updated" date={advertiserSalesUpdateDate} />

                                <Divider orientation="vertical" sx={{ height: 14 }} />
                            </Fragment>
                        )}
                    </Row>
                </Row>

                {advertiser && (
                    <Row justifyContent="flex-end" flex={1} sx={{ pr: 2 }}>
                        <Printable show={false}>
                            <>
                                {hasOneAdvertiser && (
                                    <>
                                        <Button
                                            component={Link}
                                            variant="outlined"
                                            size="medium"
                                            color="primary"
                                            to={`/crmUpload/${advertiser.id}`}
                                            target="_blank"
                                        >
                                            Upload CRM Data
                                        </Button>
                                    </>
                                )}
                            </>
                        </Printable>

                        {showExportButton && <AttributionExportMenuButton />}

                        {hasOneAdvertiser && (
                            <Printable show={false}>
                                <AdvertiserMatchbackMoreMenu />
                            </Printable>
                        )}
                    </Row>
                )}
            </Row>

            <Printable show={true}>
                <>
                    {hasOneAdvertiser && advertiser && (
                        <Row justifyContent="center" alignItems="center">
                            <Typography variant="h4">{advertiser.dealerName}</Typography>
                        </Row>
                    )}
                    <Row justifyContent="center" alignItems="center">
                        <Typography variant="h5">
                            {moment(Utils.getDate(attributionDate)).format('MMMM YYYY')}
                        </Typography>
                    </Row>
                </>
            </Printable>

            <Box>
                <Row justifyContent="space-between" alignItems="center" spacing={2}>
                    <Column gap={0.5}>
                        <Row gap={0.5} alignItems="center">
                            <SectionHeading divider={false}>Overview</SectionHeading>
                        </Row>
                    </Column>
                </Row>
            </Box>

            {attributionStatus && attributionDate && (
                <Box>
                    <AdvertiserMatchbackMetrics />
                </Box>
            )}

            <Box>
                <PolkMatchbackDataTabs />
            </Box>
        </Column>
    );
}
